import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import RmbgIcon from "../../../../assets/VideoEditing/icons/RmbgIcon";
import DownloadIcon from "../../../../assets/images/icons/DownloadIcon";
import BriaButton from "../../../../components/common/BriaButton/BriaButton";
import BriaButtonGroup from "../../../../components/common/BriaButtonGroup/BriaButtonGroup";
import { fileDownload } from "../../../../helpers/files";
import { PlaygroundResult } from "../../../../models/playground";
import { VideoEditingConfigType } from "../../../../models/video-editing";
import useImageUtils from "../../../../utils/useImageUtils";
import PlaygroundVideo from "../../../Playground/VideoGeneration/Video/PlaygroundVideo";
import { ColorOption } from "../Config/SolidBackground/VideoSolidBackgroundConfig";
import styles from "./VideoEditingResult.module.scss";

type Props = {
	playgroundResult: PlaygroundResult;
};

const VideoEditingResult = ({ playgroundResult }: Props) => {
	const loading = !playgroundResult.video?.src;

	return (
		<Box className={styles.result}>
			<VideoEditingResultHeader playgroundResult={playgroundResult} />
			{playgroundResult.video && (
				<PlaygroundVideo
					src={playgroundResult.video.src}
					className={styles.video}
					loading={loading}
					autoPlay={false}
					asyncProps={playgroundResult.video?.asyncProps}
				/>
			)}
		</Box>
	);
};

export default observer(VideoEditingResult);

const VideoEditingResultHeader = ({ playgroundResult }: { playgroundResult: PlaygroundResult }) => {
	return (
		<Box className={styles.resultHeader}>
			<VideoEditingResultAppliedTools playgroundResult={playgroundResult} />
			<VideoEditingResultActionButtons playgroundResult={playgroundResult} />
		</Box>
	);
};

const VideoEditingResultAppliedTools = ({ playgroundResult }: { playgroundResult: PlaygroundResult }) => {
	const { t } = useTranslation("video_editing", { keyPrefix: "lab.results.header.appliedTools" });
	const { t: tIncreaseResolution } = useTranslation("video_editing", {
		keyPrefix: "lab.config.tools.increase_resolution.config",
	});
	const config = playgroundResult.config as VideoEditingConfigType;
	const [maskImage, setMaskImage] = useState<string>("");
	const { generateVideoThumbnail } = useImageUtils();

	useEffect(() => {
		if (config.tool === "erase" && config.tool_config?.erase?.mask) {
			generateVideoThumbnail(config.tool_config?.erase?.mask).then((thumbnail) => {
				setMaskImage(thumbnail);
			});
		}
	}, [config.tool_config?.erase?.mask]);

	return (
		<>
			{config.tool === "erase" && (
				<Box className={styles.appliedToolContainer}>
					<LazyLoadImage
						src={maskImage}
						alt="mask"
						className={styles.maskImage}
						loading="lazy"
						threshold={100}
						effect="blur"
					/>
					<Typography className={styles.appliedTool}>{t("erase.label")}</Typography>
				</Box>
			)}
			{config.tool === "remove_background" && (
				<Box className={styles.appliedToolContainer}>
					<RmbgIcon className={styles.icon} />
					<Typography className={styles.appliedTool}>{t("remove_background.label")}</Typography>
				</Box>
			)}
			{config.tool === "solid_background" && (
				<Box className={styles.appliedToolContainer}>
					<ColorOption
						size={26}
						color={config.tool_config?.solid_background?.background_color}
						disablePointerEvents
					/>
					<Typography className={styles.appliedTool}>{t("solid_background.label")}</Typography>
				</Box>
			)}
			{config.tool === "increase_resolution" && (
				<Box className={styles.appliedToolContainer}>
					<Box className={styles.desiredIncrease}>
						{tIncreaseResolution(config.tool_config?.increase_resolution?.desired_increase || "X")}
					</Box>
					<Typography className={styles.appliedTool}>{t("increase_resolution.label")}</Typography>
				</Box>
			)}
		</>
	);
};

const VideoEditingResultActionButtons = ({ playgroundResult }: { playgroundResult: PlaygroundResult }) => {
	const { t } = useTranslation("video_editing", { keyPrefix: "lab.results.header.actionButtons" });

	const handleDownloadVideo = () => {
		if (playgroundResult.video?.src) {
			const fileName = `video_editing_${new Date()
				.toISOString()
				.replace("T", "_")
				.replace(/:/g, "-")
				.substring(0, 19)}`;
			fileDownload(playgroundResult.video.src, fileName, "mp4");
		}
	};

	return (
		<>
			<BriaButtonGroup>
				<BriaButton
					buttonType="textSmall"
					startIcon={<DownloadIcon />}
					onClick={handleDownloadVideo}
					disabled={!playgroundResult.video?.src}
				>
					{t("download")}
				</BriaButton>
			</BriaButtonGroup>
		</>
	);
};
